import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, MessageSquare, Mail, Phone } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { api } from '../services/api';

const FaleConosco = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm();

  // Máscara: (00) 0000-0000 ou (00) 00000-0000 — máx. 11 dígitos
  const formatTelefone = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 2) {
      return numbers;
    }
    if (numbers.length <= 6) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    }
    if (numbers.length <= 10) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    }
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    
    try {
      await api.post('/fale-conosco/publico', {
        nome: data.nome,
        email: data.email,
        telefone: data.telefone,
        assunto: data.assunto,
        mensagem: data.mensagem
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao enviar mensagem');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="main-container flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100">
              <MessageSquare className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="mt-6 text-center text-2xl font-extrabold text-gray-900">
              Mensagem Enviada
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Sua mensagem foi enviada com sucesso. 
              Entraremos em contato em breve.
            </p>
          </div>
          
          <div className="text-center">
            <button
              onClick={() => navigate('/login')}
              className="btn-primary"
            >
              Voltar ao Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
            <Building2 className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Fale Conosco
          </h2>
          <p className="mt-2 text-lg text-gray-600">
            Estamos aqui para ajudar. Entre em contato conosco.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Informações de Contato
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Mail className="h-5 w-5 text-primary-600 mt-1" />
                  <div>
                    <p className="font-medium text-gray-900">Email</p>
                    <p className="text-gray-600">contatolizsoftware@gmail.com</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Phone className="h-5 w-5 text-primary-600 mt-1" />
                  <div>
                    <p className="font-medium text-gray-900">Telefone</p>
                    <a href="tel:+5571981512769" className="text-gray-600 hover:text-primary-600">
                      (71) 98151-2769
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Formulário */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              Envie sua Mensagem
            </h3>
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label htmlFor="nome" className="label">
                  Nome Completo *
                </label>
                <input
                  {...register('nome', {
                    required: 'Nome é obrigatório',
                    minLength: {
                      value: 2,
                      message: 'Nome deve ter pelo menos 2 caracteres'
                    }
                  })}
                  type="text"
                  className={errors.nome ? 'input-error' : 'input'}
                  placeholder="Seu nome completo"
                />
                {errors.nome && (
                  <p className="mt-1 text-sm text-red-600">{errors.nome.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="label">
                  Email *
                </label>
                <input
                  {...register('email', {
                    required: 'Email é obrigatório',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Email inválido'
                    }
                  })}
                  type="email"
                  className={errors.email ? 'input-error' : 'input'}
                  placeholder="seu@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="telefone" className="label">
                  Telefone
                </label>
                <input
                  {...register('telefone')}
                  type="tel"
                  inputMode="numeric"
                  className="input"
                  placeholder="(11) 99999-9999"
                  maxLength={15}
                  onChange={(e) => {
                    const formatted = formatTelefone(e.target.value);
                    e.target.value = formatted;
                    setValue('telefone', formatted, { shouldValidate: false });
                  }}
                />
              </div>

              <div>
                <label htmlFor="assunto" className="label">
                  Assunto *
                </label>
                <select
                  {...register('assunto', {
                    required: 'Assunto é obrigatório'
                  })}
                  className={errors.assunto ? 'input-error' : 'input'}
                >
                  <option value="">Selecione um assunto</option>
                  <option value="suporte">Suporte Técnico</option>
                  <option value="dúvidas">Dúvidas sobre o Sistema</option>
                  <option value="sugestoes">Sugestões</option>
                  <option value="reclamacoes">Reclamações</option>
                  <option value="parcerias">Parcerias</option>
                  <option value="outros">Outros</option>
                </select>
                {errors.assunto && (
                  <p className="mt-1 text-sm text-red-600">{errors.assunto.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="mensagem" className="label">
                  Mensagem *
                </label>
                <textarea
                  {...register('mensagem', {
                    required: 'Mensagem é obrigatória',
                    minLength: {
                      value: 10,
                      message: 'Mensagem deve ter pelo menos 10 caracteres'
                    }
                  })}
                  rows={5}
                  className={errors.mensagem ? 'input-error' : 'input'}
                  placeholder="Descreva sua mensagem aqui..."
                />
                {errors.mensagem && (
                  <p className="mt-1 text-sm text-red-600">{errors.mensagem.message}</p>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex justify-center items-center"
              >
                {loading ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : null}
                {loading ? 'Enviando...' : 'Enviar Mensagem'}
              </button>
            </form>
          </div>
        </div>

        <div className="text-center">
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-500"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Voltar ao Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default FaleConosco;